import React, { useEffect, useMemo } from "react";
import { useTable, usePagination, useSortBy, useFilters } from "react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";
import { ColumnFilter } from "./ColumnFilter";
import { ColumnSort } from "./ColumnSort";
import { InfoDiv } from "./styles";
import PaginationBar from "./PaginationBar";

export interface ITable<T> {
  columns: any;
  data: T[];
  isLoading: boolean;
  onPageChange?(page: number): void;
  onRowsPerPageChange?(limit: number): void;
  total?: number;
  givenPage?: number;
  rowsPerPage?: number;
  /** Quando o valor mudar, volta à página 0 (modo cliente). Preserva ordenação da tabela. */
  paginationResetKey?: string | number;
  /** Mensagem quando não há linhas e `isLoading` é false (ex.: filtros sem resultado). */
  emptyStateMessage?: string;
}

export function TableComponent<T extends readonly object[]>({
  columns,
  data,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
  total,
  givenPage,
  rowsPerPage,
  paginationResetKey,
  emptyStateMessage,
}: ITable<T>) {
  const columnData = useMemo(() => columns, [columns]);
  const rowData = useMemo(() => data, [data]);
  const defaultColumn = useMemo(
    () => ({
      Filter: ColumnFilter,
    }),
    []
  );
  // Sem isso, o react-table usa o INDICE da linha como key (row.id default).
  // useSortBy reordena as linhas mudando qual dado cai em cada posicao, mas o
  // React (e os onClick que fecham sobre `cell.value`/`row.original`) associa
  // handlers/estado a posicao, nao ao dado -- clicar na linha 1 podia acionar
  // a acao de outro registro que passou por aquela posicao num render
  // anterior (ex.: resultado de busca mudando). Usa o id real do dado quando
  // existe (_id do Mongo ou id), com fallback pro indice pra nao quebrar
  // tabelas cujas linhas nao tem identidade propria.
  const getRowId = (row: any, index: number) =>
    row?._id ?? row?.id ?? String(index);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    { data: rowData, defaultColumn, columns: columnData, getRowId },
    useFilters,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    if (givenPage !== undefined || paginationResetKey === undefined) return;
    gotoPage(0);
  }, [paginationResetKey, givenPage, gotoPage]);

  function filterRowsByPage(items: any[], pageSize: number, pageIndex: number) {
    if (givenPage !== undefined) return items;
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const filteredItems = items.slice(startIndex, endIndex);
    return filteredItems;
  }

  return (
    <div style={{ backgroundColor: "#FFF", width: "100%" }}>
      <TableContainer
        component={Paper}
        style={{ maxHeight: "calc(100vh - 40px)", minHeight: "400px" }}
      >
        <Table stickyHeader {...getTableProps()} style={{ height: "100%" }}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                style={{ width: "100%" }}
              >
                {headerGroup.headers.map((column) => (
                  <TableCell
                    {...column.getHeaderProps({
                      ...column.getSortByToggleProps(),
                      style: !!column.width
                        ? {
                            width: column.width,
                          }
                        : undefined,
                    })}
                  >
                    <span style={{ fontWeight: "600", color: "#4B465C" }}>
                      {" "}
                      {column.render("Header")}
                    </span>

                    {column.canSort ? <ColumnSort column={column} /> : null}

                    {column.canFilter ? column.render("Filter") : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          {data.length > 0 ? (
            <TableBody {...getTableBodyProps()}>
              {filterRowsByPage(
                rows,
                rowsPerPage || pageSize,
                givenPage !== undefined ? givenPage : pageIndex
              ).map((row) => {
                prepareRow(row);
                return (
                  <TableRow
                    style={{ verticalAlign: "top" }}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell: any) => (
                      <TableCell {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : isLoading ? (
            <InfoDiv>
              <CircularProgress />
            </InfoDiv>
          ) : (
            <InfoDiv>{emptyStateMessage ?? "Sem dados para listar"}</InfoDiv>
          )}
        </Table>
      </TableContainer>
      <PaginationBar
        count={total || data.length}
        page={givenPage !== undefined ? givenPage : pageIndex}
        rowsPerPage={rowsPerPage || pageSize}
        givenPage={givenPage}
        total={total}
        onPageChange={(newPage) => {
          if (onPageChange) return onPageChange(newPage);
          gotoPage(newPage);
        }}
        onRowsPerPageChange={(newSize) => {
          if (onRowsPerPageChange) return onRowsPerPageChange(newSize);
          setPageSize(newSize);
        }}
      />
    </div>
  );
}

export default TableComponent;
